import { escapeIlike, UNIVERSE_COLUMNS } from './queries.js';
/** The six knowledge-loop stages in display order; matches the kb schema's stage checks. */
export const KB_STAGES = ['Capture', 'Understand', 'Anticipate', 'Decide', 'Learn', 'Foundation'];
/** The Notion database the kb copy is synced from (hourly); Notion is where solutions are edited. */
export const SOLUTIONS_REPOSITORY_URL = 'https://www.notion.so/2ca87505ce0880bd9605e84d0c663b87';
/** True for a canonical UUID string. */
export function isUuid(s) {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);
}
/** A kb uuid, a Notion page id (what Lupe's chat cards carry), or neither. */
export function classifySolutionId(id) {
    if (isUuid(id))
        return 'uuid';
    return /^[0-9a-f]{32}$/i.test(id) ? 'notion' : null;
}
const MAX_QUERY_LENGTH = 200;
/** Search text safe to put inside a PostgREST or() filter, capped at 200 characters. */
export function cleanSolutionQuery(q) {
    return escapeIlike(q).slice(0, MAX_QUERY_LENGTH).trimEnd();
}
const live = (x) => !!x && x.archived_at === null;
const ref = (s) => ({ id: s.id, name: s.name });
const byName = (a, b) => a.name.localeCompare(b.name);
/** Shapes one solution row (plus its client and people → persons ids) for a solution page. Archived links and vocabulary are dropped. */
export function toSolutionDetail(row, ctx) {
    const out = (kind) => row.outgoing.filter((l) => l.kind === kind).map((l) => l.target).filter(live).map(ref).sort(byName);
    const stages = new Set(row.solution_stages.map((s) => s.stage));
    return {
        id: row.id,
        name: row.name,
        type: row.type,
        notionUrl: row.notion_url,
        archived: row.archived_at !== null,
        client: ctx.client,
        builtBy: row.built_by,
        lifecycle: row.lifecycle,
        size: row.size,
        replicability: row.replicability,
        summary: row.summary,
        problem: row.problem,
        approach: row.approach,
        gains: row.gains,
        learnings: row.learnings,
        stages: KB_STAGES.filter((s) => stages.has(s)),
        capabilities: row.solution_capabilities.map((c) => c.capabilities).filter(live).map((c) => c.name).sort(),
        verticals: row.solution_verticals
            .filter((v) => live(v.verticals))
            .map((v) => ({ name: v.verticals.name, primary: v.primary }))
            .sort((a, b) => Number(b.primary) - Number(a.primary) || a.name.localeCompare(b.name)),
        tools: row.solution_tools.map((t) => t.tools).filter(live).map((t) => t.name).sort(),
        people: row.solution_people
            .map((p) => ({ name: p.name, current: p.current, personId: (p.person_id && ctx.personIds.get(p.person_id)) || null }))
            .sort((a, b) => Number(b.current) - Number(a.current) || a.name.localeCompare(b.name)),
        partOf: out('part_of'),
        features: row.incoming.filter((l) => l.kind === 'part_of').map((l) => l.source).filter(live).map(ref).sort(byName),
        dependsOn: out('depends_on'),
        derivedFrom: out('derived_from'),
        supersedes: out('supersedes'),
        related: [...out('related'), ...row.incoming.filter((l) => l.kind === 'related').map((l) => l.source).filter(live).map(ref)]
            .filter((r, i, all) => all.findIndex((x) => x.id === r.id) === i)
            .sort(byName),
    };
}
// solution_stages is a view (no FK), so it is read with a second query.
const SOLUTION_SELECT = `id, name, type, notion_url, archived_at, client_id, built_by, lifecycle, size, replicability,
  summary, problem, approach, gains, learnings,
  solution_capabilities(capabilities(name, archived_at)),
  solution_verticals(primary, verticals(name, archived_at)),
  solution_tools(tools(name, archived_at)),
  solution_people(name, current, person_id),
  outgoing:solution_links!from_id(kind, target:solutions!to_id(id, name, archived_at)),
  incoming:solution_links!to_id(kind, source:solutions!from_id(id, name, archived_at))`;
function dataOf(r) {
    if (r.error)
        throw new Error(r.error.message);
    return r.data;
}
/** Clients by id (kb rows point at public.clients, which PostgREST cannot embed across schemas). */
async function clientsById(db, ids) {
    if (!ids.length)
        return new Map();
    const rows = dataOf(await db.from('clients').select(`id, ${UNIVERSE_COLUMNS.linkedClient}`).in('id', ids));
    return new Map((rows ?? []).map((c) => [c.id, { prefix: c.prefix, name: c.name }]));
}
/** Live kb solutions matching the query by full text or name, as solution entities; [] without a request when nothing searchable is left. */
export async function searchSolutions(db, q, limit) {
    const term = cleanSolutionQuery(q);
    if (!term)
        return [];
    const rows = dataOf(await db
        .schema('kb')
        .from('solutions')
        .select('id, name, client_id')
        .or(`search.wfts(english).${term},name.ilike.*${term}*`)
        .is('archived_at', null)
        .order('name')
        .limit(limit)) ?? [];
    const clients = await clientsById(db, [...new Set(rows.flatMap((r) => (r.client_id ? [r.client_id] : [])))]);
    return rows.map((r) => {
        const e = { kind: 'solution', id: r.id, title: r.name };
        const client = r.client_id ? clients.get(r.client_id) : undefined;
        if (client)
            e.subtitle = client.name;
        return e;
    });
}
/** One solution by kb uuid or Notion page id, archived included; null when unknown, malformed or hidden by RLS. */
export async function solutionById(db, id) {
    const kind = classifySolutionId(id);
    if (!kind)
        return null;
    const found = dataOf(await db
        .schema('kb')
        .from('solutions')
        .select(SOLUTION_SELECT)
        .eq(kind === 'uuid' ? 'id' : 'notion_page_id', kind === 'uuid' ? id : id.toLowerCase())
        .maybeSingle());
    if (!found)
        return null;
    const personRowIds = found.solution_people.flatMap((p) => (p.person_id ? [p.person_id] : []));
    const [clients, people, stages] = await Promise.all([
        clientsById(db, found.client_id ? [found.client_id] : []),
        personRowIds.length ? db.from('people').select('id, person_id').in('id', personRowIds) : Promise.resolve({ data: [], error: null }),
        db.schema('kb').from('solution_stages').select('stage').eq('solution_id', found.id),
    ]);
    const personIds = new Map((dataOf(people) ?? []).flatMap((p) => (p.person_id ? [[p.id, p.person_id]] : [])));
    const row = { ...found, solution_stages: dataOf(stages) ?? [] };
    return toSolutionDetail(row, { client: found.client_id ? (clients.get(found.client_id) ?? null) : null, personIds });
}
const LIST_SELECT = `id, name, type, summary, client_id, lifecycle, replicability, primitive_candidate,
  solution_capabilities(capabilities(name, archived_at)),
  solution_verticals(verticals(name, archived_at)),
  incoming:solution_links!to_id(kind, source:solutions!from_id(id, name, archived_at))`;
// PostgREST caps a response at 1000 rows, so the unfiltered stages view is read page by page.
const PAGE = 1000;
async function allStages(db) {
    const out = [];
    for (let from = 0;; from += PAGE) {
        const page = dataOf(await db.schema('kb').from('solution_stages').select('solution_id, stage').order('solution_id').order('stage').range(from, from + PAGE - 1)) ?? [];
        out.push(...page);
        if (page.length < PAGE)
            return out;
    }
}
/** Every live kb solution and feature with its facets for the library, sorted by name. */
export async function listSolutions(db) {
    const [solutions, stages] = await Promise.all([
        db.schema('kb').from('solutions').select(LIST_SELECT).is('archived_at', null).order('name'),
        allStages(db),
    ]);
    const rows = dataOf(solutions) ?? [];
    const stagesBy = new Map();
    for (const s of stages)
        stagesBy.set(s.solution_id, (stagesBy.get(s.solution_id) ?? new Set()).add(s.stage));
    const clients = await clientsById(db, [...new Set(rows.flatMap((r) => (r.client_id ? [r.client_id] : [])))]);
    return rows
        .map((r) => ({
        id: r.id,
        name: r.name,
        type: r.type,
        summary: r.summary,
        client: r.client_id ? (clients.get(r.client_id) ?? null) : null,
        lifecycle: r.lifecycle,
        stages: KB_STAGES.filter((s) => stagesBy.get(r.id)?.has(s)),
        capabilities: r.solution_capabilities.map((c) => c.capabilities).filter(live).map((c) => c.name).sort(),
        verticals: r.solution_verticals.map((v) => v.verticals).filter(live).map((v) => v.name).sort(),
        reusable: r.primitive_candidate ||
            r.replicability === 'High' ||
            r.incoming.some((l) => l.kind === 'derived_from' && live(l.source)),
    }))
        .sort(byName);
}
/** When the newest kb sync finished (ISO), or null before the first run. */
export async function lastKbSync(db) {
    const rows = dataOf(await db.schema('kb').from('sync_runs').select('finished_at').order('finished_at', { ascending: false }).limit(1));
    return rows?.[0]?.finished_at ?? null;
}
/** Ids of kb solutions a person worked on, through their L3OS teammate rows; [] when they are not a teammate. */
export async function solutionIdsForPerson(db, personsId) {
    const teammates = dataOf(await db.from('people').select('id').eq('person_id', personsId)) ?? [];
    if (!teammates.length)
        return [];
    const rows = dataOf(await db.schema('kb').from('solution_people').select('solution_id').in('person_id', teammates.map((t) => t.id))) ?? [];
    return [...new Set(rows.map((r) => r.solution_id))];
}
